import * as THREE from 'three';
import { CLASSICAL, HEAL_GLOW, INDUSTRIAL, MODERN } from '../materials';
import { Beam, Figure, type ModelProps, Pole } from '../parts';

/**
 * Support units that mend rather than fight (healer, medic, engineer): each
 * sits on y = 0, faces +z, at real proportions (UnitModel scales them). Their
 * healing marks are a green plus or orb (`HEAL_GLOW`), never a red cross.
 */

const { SKIN, WOOD, DARK_WOOD, WOOL } = CLASSICAL;
const { HARD_HAT, DENIM, STEEL } = INDUSTRIAL;
const { OLIVE, DARK_OLIVE, WHITE } = MODERN;

const G = {
  head: new THREE.SphereGeometry(0.075, 10, 8),
  torso: new THREE.CylinderGeometry(0.085, 0.12, 0.3, 8),
  arm: new THREE.CylinderGeometry(0.024, 0.021, 0.17, 6),
  // Healer
  robe: new THREE.CylinderGeometry(0.075, 0.14, 0.34, 8),
  mantle: new THREE.CylinderGeometry(0.086, 0.1, 0.05, 8),
  satchel: new THREE.BoxGeometry(0.035, 0.08, 0.075),
  satchelFlap: new THREE.BoxGeometry(0.04, 0.03, 0.08),
  orb: new THREE.IcosahedronGeometry(0.04, 1),
  orbCradle: new THREE.TorusGeometry(0.03, 0.007, 4, 10).rotateX(Math.PI / 2),
  // Medic
  helmet: new THREE.SphereGeometry(0.084, 10, 5, 0, Math.PI * 2, 0, Math.PI / 2),
  helmetRim: new THREE.CylinderGeometry(0.092, 0.092, 0.012, 10),
  armband: new THREE.CylinderGeometry(0.029, 0.028, 0.035, 6),
  belt: new THREE.CylinderGeometry(0.112, 0.116, 0.03, 8),
  pack: new THREE.BoxGeometry(0.14, 0.15, 0.07),
  plusBar: new THREE.BoxGeometry(0.07, 0.022, 0.008),
  medkit: new THREE.BoxGeometry(0.06, 0.045, 0.035),
  // Engineer
  hardHat: new THREE.SphereGeometry(0.084, 10, 6, 0, Math.PI * 2, 0, Math.PI / 2),
  hatBrim: new THREE.CylinderGeometry(0.105, 0.105, 0.012, 12),
  toolBelt: new THREE.CylinderGeometry(0.114, 0.118, 0.03, 8),
  pouch: new THREE.BoxGeometry(0.045, 0.05, 0.03),
  wrenchHead: new THREE.BoxGeometry(0.1, 0.04, 0.03),
  wrenchJaw: new THREE.BoxGeometry(0.025, 0.05, 0.03),
};

// Where the medic's pack sits on his back (its emblem is on the outer face).
const PACK: [number, number, number] = [0, 0.22, -0.12];
const PACK_BACK = PACK[2] - 0.035 - 0.004;

/** A green plus: two crossed bars facing ±z, centred on `position`. */
function Plus({ position }: { position: [number, number, number] }) {
  return (
    <group position={position}>
      <mesh geometry={G.plusBar} material={HEAL_GLOW} />
      <mesh geometry={G.plusBar} material={HEAL_GLOW} rotation={[0, 0, Math.PI / 2]} />
    </group>
  );
}

/** Arms hanging at the sides, slightly out from the body, in `material`. */
function Arms({ material }: { material: THREE.Material }) {
  return (
    <>
      {[-1, 1].map((side) => (
        <mesh
          key={side}
          geometry={G.arm}
          material={material}
          position={[side * 0.115, 0.2, 0]}
          rotation={[0, 0, side * 0.15]}
        />
      ))}
    </>
  );
}

/**
 * A long team-coloured robe under a wool mantle, a leather satchel on a strap
 * across the chest, and a tall staff topped by a small glowing green orb.
 */
export function Healer({ accent }: ModelProps) {
  return (
    <>
      <mesh geometry={G.robe} material={accent} position={[0, 0.17, 0]} />
      <mesh geometry={G.mantle} material={WOOL} position={[0, 0.31, 0]} />
      <mesh geometry={G.head} material={SKIN} position={[0, 0.39, 0]} />
      <Beam
        from={[0.06, 0.31, 0.06]}
        to={[-0.1, 0.2, 0.07]}
        material={DARK_WOOD}
        thickness={0.014}
      />
      <mesh geometry={G.satchel} material={WOOD} position={[-0.125, 0.17, 0.03]} />
      <mesh geometry={G.satchelFlap} material={DARK_WOOD} position={[-0.127, 0.2, 0.03]} />
      <Pole length={0.6} position={[0.15, 0.3, 0.03]} material={DARK_WOOD} />
      <mesh geometry={G.orbCradle} material={WOOD} position={[0.15, 0.6, 0.03]} />
      <mesh geometry={G.orb} material={HEAL_GLOW} position={[0.15, 0.64, 0.03]} />
    </>
  );
}

/**
 * A field medic in olive with a team-coloured armband and belt, a combat
 * helmet, a white backpack with a green plus, and a medkit in hand.
 */
export function Medic({ accent }: ModelProps) {
  return (
    <>
      <mesh geometry={G.torso} material={OLIVE} position={[0, 0.15, 0]} />
      <mesh geometry={G.belt} material={accent} position={[0, 0.1, 0]} />
      <Arms material={OLIVE} />
      <mesh
        geometry={G.armband}
        material={accent}
        position={[-0.118, 0.235, 0]}
        rotation={[0, 0, -0.15]}
      />
      <mesh geometry={G.head} material={SKIN} position={[0, 0.37, 0]} />
      <mesh geometry={G.helmet} material={DARK_OLIVE} position={[0, 0.385, 0]} />
      <mesh geometry={G.helmetRim} material={DARK_OLIVE} position={[0, 0.39, 0]} />
      <mesh geometry={G.pack} material={WHITE} position={PACK} />
      <Plus position={[PACK[0], PACK[1], PACK_BACK]} />
      <mesh geometry={G.medkit} material={WHITE} position={[0.135, 0.1, 0.02]} />
      <Plus position={[0.135, 0.1, 0.039]} />
    </>
  );
}

/**
 * Team-coloured overalls over a denim shirt, a yellow hard hat, a tool belt
 * with pouches, and a big open-ended wrench over the shoulder.
 */
export function Engineer({ accent }: ModelProps) {
  return (
    <>
      <Figure accent={accent} />
      <Arms material={DENIM} />
      <mesh geometry={G.hardHat} material={HARD_HAT} position={[0, 0.39, 0]} />
      <mesh geometry={G.hatBrim} material={HARD_HAT} position={[0, 0.395, 0.01]} />
      <mesh geometry={G.toolBelt} material={DARK_WOOD} position={[0, 0.09, 0]} />
      {[-0.07, 0.07].map((x) => (
        <mesh key={x} geometry={G.pouch} material={WOOD} position={[x, 0.075, 0.095]} />
      ))}
      <group position={[0.14, 0.13, 0.05]} rotation={[-0.2, 0, 0.15]}>
        <Beam from={[0, 0, 0]} to={[0, 0.36, 0]} material={STEEL} thickness={0.025} />
        <mesh geometry={G.wrenchHead} material={STEEL} position={[0, 0.38, 0]} />
        {[-0.0375, 0.0375].map((x) => (
          <mesh key={x} geometry={G.wrenchJaw} material={STEEL} position={[x, 0.42, 0]} />
        ))}
      </group>
    </>
  );
}
